import { useState, useEffect } from 'react';
import { useDataEngine } from '@dhis2/app-runtime';
import { CONFIG } from '../../leadership/api/LeadershipServices';

// Helper to decode HTML entities like &nbsp; &amp; etc.
const decodeHTMLEntities = (text) => {
    if (!text) return '';
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
};

export const useLeadershipCompetencies = (orgUnit) => {
    const [competencies, setCompetencies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const engine = useDataEngine();

    useEffect(() => {
        let cancelled = false;

        const fetchCompetencies = async () => {
            if (!orgUnit) {
                if (!cancelled) setLoading(false);
                return;
            }

            try {
                if (!cancelled) setLoading(true);
                console.log('🔍 [LEADERSHIP] Fetching competencies for orgUnit:', orgUnit);

                const response = await engine.query({
                    competencies: {
                        resource: 'events',
                        params: {
                            program: CONFIG.program,
                            programStage: CONFIG.stages.config,
                            orgUnit: orgUnit,
                            ouMode: 'ACCESSIBLE',
                            fields: 'event,orgUnit,dataValues[dataElement,value]',
                            pageSize: 100,
                            order: 'eventDate:desc'
                        }
                    }
                });

                const events = response.competencies?.events || [];
                console.log('✅ [LEADERSHIP] Found', events.length, 'competency events');

                const mappedCompetencies = events.map(event => {
                    const dvMap = {};
                    event.dataValues?.forEach(dv => {
                        dvMap[dv.dataElement] = dv.value;
                    });

                    const rawDefinition = dvMap[CONFIG.de.competencyDefinition] || '';
                    
                    // ✅ Parse the HTML to extract clean content
                    const parsedContent = parseCompetencyHTML(rawDefinition);

                    return {
                        id: event.event,
                        name: dvMap[CONFIG.de.competencyCode] || 'Unnamed Competency',
                        description: parsedContent.description || rawDefinition,
                        weight: dvMap[CONFIG.de.weightValue] ? parseFloat(dvMap[CONFIG.de.weightValue]) : 25,
                        competencyId: dvMap[CONFIG.de.competencyId] || '',
                        orgUnit: event.orgUnit,
                        // Store parsed behavioural indicators
                        behaviouralIndicators: parsedContent.indicators,
                        // Store clean display content
                        displayContent: parsedContent.displayContent,
                         sortOrder: dvMap[CONFIG.de.sortOrder] !== undefined
                            ? parseFloat(dvMap[CONFIG.de.sortOrder])
                            : null,
                    };
                });

                const validCompetencies = mappedCompetencies.filter(c => c.name && c.name !== 'Unnamed Competency');

                 validCompetencies.sort((a, b) => {
                    if (a.sortOrder == null && b.sortOrder == null) return 0;
                    if (a.sortOrder == null) return 1;
                    if (b.sortOrder == null) return -1;
                    return a.sortOrder - b.sortOrder;
                });

                if (validCompetencies.length === 0) {
                    console.log('⚠️ [LEADERSHIP] No competencies found, using defaults');
                    if (!cancelled) setCompetencies(getDefaultCompetencies());
                } else {
                    console.log('✅ [LEADERSHIP] Loaded', validCompetencies.length, 'competencies');
                    if (!cancelled) setCompetencies(validCompetencies);
                }

                if (!cancelled) setError(null);
            } catch (err) {
                console.error('❌ [LEADERSHIP] Failed to fetch competencies:', err);
                if (!cancelled) {
                    setError(err.message);
                    setCompetencies(getDefaultCompetencies());
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchCompetencies();

        return () => {
            cancelled = true;
        };
    }, [orgUnit, engine]);

    return { competencies, loading, error };
};

// ✅ Helper function to parse HTML content from the database
const parseCompetencyHTML = (htmlContent) => {
    if (!htmlContent) {
        return { description: '', indicators: {}, displayContent: '' };
    }

    // ✅ Step 1: Decode HTML entities first
    const decodedHTML = decodeHTMLEntities(htmlContent);
    
    // Create a DOM parser
    const parser = new DOMParser();
    const doc = parser.parseFromString(decodedHTML, 'text/html');

    let description = '';
    let indicators = {};
    let displayContent = '';

    // Get all text content (this will have &nbsp; already decoded)
    const allText = doc.body.textContent || '';
    
    // 🔍 DEBUG: Log the decoded content
    console.log('📝 [PARSER] Decoded HTML:', decodedHTML);
    console.log('📝 [PARSER] All text:', allText);

    // First, try to extract the main description (everything before "Observable Behaviours")
    const behaviourIndex = allText.toLowerCase().indexOf('observable behaviours');
    if (behaviourIndex > 0) {
        const descPart = allText.substring(0, behaviourIndex).trim();
        if (descPart) {
            description = descPart;
        }
    }

    // ✅ IMPROVED: Extract behavioural indicators using multiple strategies
    let foundIndicators = {};
    
    // Strategy 1: Look for patterns like "5: description" or "5  description" or "5. description"
    const lines = allText.split('\n');
    
    lines.forEach(line => {
        const trimmedLine = line.trim();
        if (!trimmedLine) return;
        
        // ✅ Clean up any remaining &nbsp; or multiple spaces
        const cleanLine = trimmedLine.replace(/\s+/g, ' ');
        
        // Try multiple patterns
        const patterns = [
            /^(\d+)\s*[:.\s]+(.+)/,      // "5: description" or "5. description" or "5  description"
            /^(\d+)\s{2,}(.+)/,          // "5    description" (multiple spaces)
            /^(\d+)\s*[-]\s*(.+)/,       // "5 - description"
            /^(\d+)\s*\)\s*(.+)/,        // "5) description"
            /^(\d+)\s*\.\s*(.+)/,        // "5. description"
        ];
        
        let matched = false;
        for (const pattern of patterns) {
            const match = cleanLine.match(pattern);
            if (match) {
                const level = parseInt(match[1]);
                const indicatorText = match[2].trim();
                if (level >= 1 && level <= 5 && indicatorText && indicatorText.length > 3) {
                    foundIndicators[level] = indicatorText;
                    matched = true;
                    console.log(`✅ [PARSER] Found level ${level}: "${indicatorText.substring(0, 30)}..."`);
                    break;
                }
            }
        }
        
        // If no pattern matched, check if line contains a number at the start
        if (!matched) {
            const numberMatch = cleanLine.match(/^(\d+)\s*/);
            if (numberMatch) {
                const level = parseInt(numberMatch[1]);
                if (level >= 1 && level <= 5) {
                    const indicatorText = cleanLine.replace(/^\d+\s*/, '').trim();
                    if (indicatorText && indicatorText.length > 3) {
                        foundIndicators[level] = indicatorText;
                        console.log(`✅ [PARSER] Found level ${level} (fallback): "${indicatorText.substring(0, 30)}..."`);
                    }
                }
            }
        }
    });

    // Strategy 2: Try to extract from div elements (for LEARNING format)
    if (Object.keys(foundIndicators).length < 5) {
        console.log('⚠️ [PARSER] Not all levels found, trying div extraction...');
        
        const divs = doc.querySelectorAll('div');
        divs.forEach(el => {
            const text = el.textContent?.trim() || '';
            if (!text) return;
            
            // Clean up the text
            const cleanText = text.replace(/\s+/g, ' ');
            
            // Check if this div contains level descriptions
            const match = cleanText.match(/(\d+)\s*[:.\s]+(.+)/);
            if (match) {
                const level = parseInt(match[1]);
                const indicatorText = match[2].trim();
                if (level >= 1 && level <= 5 && indicatorText && indicatorText.length > 3) {
                    if (!foundIndicators[level]) {
                        foundIndicators[level] = indicatorText;
                        console.log(`✅ [PARSER] Found level ${level} from div: "${indicatorText.substring(0, 30)}..."`);
                    }
                }
            }
        });
    }

    // ✅ Strategy 3: Handle EXCELLENCE format (all in one div with <br> tags)
    if (Object.keys(foundIndicators).length < 5) {
        console.log('⚠️ [PARSER] Still missing levels, trying <br> split extraction...');
        
        // Split by <br> tags on the decoded HTML
        const brLines = decodedHTML.split(/<br\s*\/?>/i);
        brLines.forEach(line => {
            // Remove HTML tags and clean up
            let cleanText = line.replace(/<[^>]*>/g, '').trim();
            if (!cleanText) return;
            
            // Clean up multiple spaces and &nbsp;
            cleanText = cleanText.replace(/\s+/g, ' ');
            
            // Try to find level patterns
            const match = cleanText.match(/(\d+)\s*[:.\s]+(.+)/);
            if (match) {
                const level = parseInt(match[1]);
                const indicatorText = match[2].trim();
                if (level >= 1 && level <= 5 && indicatorText && indicatorText.length > 3) {
                    if (!foundIndicators[level]) {
                        foundIndicators[level] = indicatorText;
                        console.log(`✅ [PARSER] Found level ${level} from <br> split: "${indicatorText.substring(0, 30)}..."`);
                    }
                }
            } else {
                // Try to find just a number at the start
                const numberMatch = cleanText.match(/^(\d+)\s*/);
                if (numberMatch) {
                    const level = parseInt(numberMatch[1]);
                    if (level >= 1 && level <= 5) {
                        const indicatorText = cleanText.replace(/^\d+\s*/, '').trim();
                        if (indicatorText && indicatorText.length > 3) {
                            if (!foundIndicators[level]) {
                                foundIndicators[level] = indicatorText;
                                console.log(`✅ [PARSER] Found level ${level} from <br> split (fallback): "${indicatorText.substring(0, 30)}..."`);
                            }
                        }
                    }
                }
            }
        });
    }

    // Strategy 4: Try to extract from raw HTML string with regex
    if (Object.keys(foundIndicators).length < 5) {
        console.log('⚠️ [PARSER] Still missing levels, trying raw HTML regex extraction...');
        
        // Look for patterns like "5&nbsp; &nbsp; description" or "5<br>description"
        const regexPatterns = [
            /(\d+)&nbsp;\s*&nbsp;\s*([^<]+)/g,  // "5&nbsp; &nbsp; description"
            /(\d+)\s*<br\s*\/?>\s*([^<]+)/g,    // "5<br>description"
            /(\d+)\s*<div[^>]*>([^<]+)<\/div>/g, // "5<div>description</div>"
        ];
        
        regexPatterns.forEach(pattern => {
            let match;
            while ((match = pattern.exec(decodedHTML)) !== null) {
                const level = parseInt(match[1]);
                let indicatorText = match[2].trim();
                // Clean up any remaining HTML and &nbsp;
                indicatorText = indicatorText.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
                if (level >= 1 && level <= 5 && indicatorText && indicatorText.length > 3) {
                    if (!foundIndicators[level]) {
                        foundIndicators[level] = indicatorText;
                        console.log(`✅ [PARSER] Found level ${level} from regex: "${indicatorText.substring(0, 30)}..."`);
                    }
                }
            }
        });
    }

    // ✅ Ensure all 5 levels are present (fill missing ones with fallbacks if needed)
    const requiredLevels = [5, 4, 3, 2, 1];
    const defaultIndicators = {
        5: 'Exceptional performance - exceeds all expectations',
        4: 'Exceeds expectations - consistently delivers high quality',
        3: 'Meets expectations - demonstrates solid, reliable performance',
        2: 'Needs improvement - requires guidance in some areas',
        1: 'Unsatisfactory - significant improvement required'
    };
    
    // Check if we have any indicators at all
    let hasAnyIndicators = false;
    requiredLevels.forEach(level => {
        if (foundIndicators[level] && foundIndicators[level].length > 5) {
            hasAnyIndicators = true;
        }
    });
    
    // Only use defaults if we found NO indicators at all
    if (!hasAnyIndicators) {
        console.log('⚠️ [PARSER] No indicators found, using defaults');
        foundIndicators = { ...defaultIndicators };
    }

    // Sort by level descending (5, 4, 3, 2, 1)
    const sortedIndicators = {};
    requiredLevels.forEach(level => {
        if (foundIndicators[level]) {
            sortedIndicators[level] = foundIndicators[level];
        }
    });

    indicators = sortedIndicators;

    // Clean up description - remove any "Observable Behaviours" and indicator text
    if (description) {
        const obsIndex = description.toLowerCase().indexOf('observable behaviours');
        if (obsIndex > 0) {
            description = description.substring(0, obsIndex).trim();
        }
        // Remove any indicator patterns from description
        requiredLevels.forEach(level => {
            const regex = new RegExp(`${level}\\s*[:.\\s]+[^\\n]+`, 'g');
            description = description.replace(regex, '');
        });
        description = description.trim();
    }

    // If description is still empty, try to get the first meaningful text
    if (!description) {
        const firstDiv = doc.querySelector('div');
        if (firstDiv) {
            const text = firstDiv.textContent?.trim() || '';
            if (!/^\d/.test(text)) {
                description = text;
            }
        }
    }

    // Build clean display content
    displayContent = description || '';

    console.log('📝 [PARSER] Final description:', description);
    console.log('📝 [PARSER] Final indicators:', indicators);

    return { description, indicators, displayContent };
};

// ✅ Default competencies with complete 5-level indicators
const getDefaultCompetencies = () => {
    return [
        {
            id: 'default-learning',
            name: 'LEARNING',
            description: 'Actively seeks and shares knowledge; embraces feedback and innovation',
            weight: 25,
            competencyId: 'default-learning',
            behaviouralIndicators: {
                5: 'Leads knowledge-sharing initiatives; actively mentors others; drives innovation',
                4: 'Regularly seeks learning; shares knowledge; applies lessons effectively',
                3: 'Participates in learning; accepts feedback; shows willingness to improve',
                2: 'Limited effort to learn or share; resists feedback; slow to adapt',
                1: 'Avoids learning; rejects feedback; does not improve or share knowledge'
            },
            displayContent: 'Actively seeks and shares knowledge; embraces feedback and innovation'
        },
        {
            id: 'default-excellence',
            name: 'EXCELLENCE',
            description: 'Delivers exceptional quality; anticipates issues; drives continuous improvement across team',
            weight: 25,
            competencyId: 'default-excellence',
            behaviouralIndicators: {
                5: 'Delivers exceptional quality; anticipates issues; drives continuous improvement across team',
                4: 'High-quality work with minimal errors; takes initiative to improve processes',
                3: 'Meets quality standards; completes work reliably and on time',
                2: 'Inconsistent quality; requires supervision or rework; misses some deadlines',
                1: 'Poor quality output; frequent errors; lacks accountability and follow-through'
            },
            displayContent: 'Delivers exceptional quality; anticipates issues; drives continuous improvement across team'
        },
        {
            id: 'default-respect',
            name: 'RESPECT',
            description: 'Champions inclusivity; proactively resolves conflicts; consistently models respect',
            weight: 25,
            competencyId: 'default-respect',
            behaviouralIndicators: {
                5: 'Champions inclusivity; proactively resolves conflicts; consistently models respect and influences others positively',
                4: 'Consistently respectful; values diversity; constructively handles disagreements',
                3: 'Demonstrates respectful behaviour; listens to others; maintains professionalism',
                2: 'Occasionally shows dismissiveness; struggles with differing views; inconsistent professionalism',
                1: 'Frequently disrespectful; interrupts, disregards others, or creates tension'
            },
            displayContent: 'Champions inclusivity; proactively resolves conflicts; consistently models respect'
        }
    ];
};